import React, { useState, useCallback, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../context/alert';
import { useUserContext } from '../../../context/user';
import { UserAppealResponse, AppealReplyRequest, AppealStatusUpdateRequest } from '../../../types/api/appeals';
import { FeedbackStatus, FeedbackStatusUpdateRequest } from '../../../types/api/feedback';
import { FileUploadResponse, UploadingFile } from '../../../types/api/file';
import { MediaResponse } from '../../../types/api/common';
import { replyToUserAppeal } from '../../../services/appeals';
import { useDeletePostsMutation } from '../../../hooks/useFileMutation';
import { useDeleteAppealFileMutation } from '../../../hooks/useUserAppealsQuery';
import { useAppealRepliesQuery } from '../../../hooks/useUserAppealsQuery';
import { updateAppealStatus } from '../../../services/appeals';
import ModalSheet from '../../basic/ModalSheet';
import FileUploadComponent from '../../common/FileUploadComponent';
import FileListComponent from '../../common/FileListComponent';
import FileListUploadedComponent from '../../common/FileListUploadedComponent';
import { FeedbackStatusButton } from '../../basic/AppealStatusButton';
import { AppealCard } from './AppealCard';
import { isAllowAction } from '../../../services/permissions/utils';
import { Stack, Textarea, FormControl, Button, Text } from '@primer/react'
import { formatDateTime } from '../../../helpers/dates';

interface CreateUserAppealAnswerModalProps {
    appeal: UserAppealResponse
    isOpen: boolean;
    onDidAnswerSended: () => void;
    onDidUserFileDeleted: () => void;
    onDidAnswerStatusChanged: () => void;
    onClose: () => void;
}

export const CreateUserAppealAnswerModal: React.FC<CreateUserAppealAnswerModalProps> = ({ appeal, isOpen, onDidAnswerSended, onDidUserFileDeleted, onDidAnswerStatusChanged, onClose }) => {

    const { profile } = useUserContext();
    const [commentText, setCommentText] = useState<string>('');
    const [appealStatus, setAppealStatus] = useState<FeedbackStatus>(FeedbackStatus.NEW);
    const [isPending, setIsPending] = useState<boolean>(false)
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [isDeletingProcess, setIsDeletingProcess] = useState<boolean>(false)
    const [files, setFiles] = useState<FileUploadResponse[]>([]);
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const [userFiles, setUserFiles] = useState<MediaResponse[]>([]);
    const deletePostsMutation = useDeletePostsMutation();
    const deleteAppealFileMutation = useDeleteAppealFileMutation()
    const { data: dataReplies, isLoading: isLoadingReplies, error: errorReplies, refetch: refetchReplies } = useAppealRepliesQuery(
        appeal.id,
        undefined,
        1,
        100,
        ['created_at']
    );
    const navigate = useNavigate();
    const { setSuccess, setError, } = useAlert();

    const handleCommentChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
        setCommentText(e.target.value);
    }, []);

    const { mutate: replyToUserAppealRequest } = useMutation({
        mutationFn: ({ request }: {
            request: AppealReplyRequest,
        }) => replyToUserAppeal(appeal.id, request),
        onSuccess: (data) => {
            setIsPending(false);
            setSuccess('Пользователю отправлен ответ на электронную почту')
            onDidAnswerSended()
        },
        onError: (_) => {
            setIsPending(false)
        }
    });

    const sendAnswer = () => {
        setIsPending(true);
        const request: AppealReplyRequest = {
            text: commentText,
            media: files.map(f => f.id)
        };

        replyToUserAppealRequest({ request })
    };

    const fileDidUpload = (file: UploadingFile | undefined) => {
        const media = file?.remoteFile

        if (file && media) {
            setFiles(prev => [...prev, media]);
            // Удалить файл из загружающихся
            setUploadingFiles(prev => prev.filter(uf => uf.file.name !== file.file.name));
        }
    }

    const handleFileDeleted = (file: FileUploadResponse) => {
        const id = file.id
        if (id) {
            setFiles(prev => prev.filter(f => f.id !== id));
        }
    };

    const handleUserFileDeleted = (media: MediaResponse) => {
        const id = media.id
        if (id) {
            setUserFiles(prev => prev.filter(f => f.id !== id));
        }
    };

    const handleUploadStart = (file: File, abortController: AbortController) => {
        const uploadingFile: UploadingFile = {
            file,
            remoteFile: undefined,
            progress: 0,
            abortController
        };
        setUploadingFiles(prev => [...prev, uploadingFile]);
    };

    const handleUploadProgress = (fileName: string, progress: number) => {
        setUploadingFiles(prev =>
            prev.map(uf =>
                uf.file.name === fileName
                    ? { ...uf, progress }
                    : uf
            )
        );
    };

    const handleUploadCancel = (fileName: string) => {
        setUploadingFiles(prev => {
            const uploadingFile = prev.find(uf => uf.file.name === fileName);
            if (uploadingFile) {
                uploadingFile.abortController.abort();
            }
            return prev.filter(uf => uf.file.name !== fileName);
        });
    };

    const handleUploadDelete = (file: UploadingFile) => {
        const id = file.remoteFile?.id
        if (id) {
            setIsDeletingProcess(true)
            deletePostsMutation.mutate(
                { file_id: id },
                {
                    onSuccess: (data) => {
                        setIsDeletingProcess(false)
                        if (file.remoteFile) {
                            handleFileDeleted(file.remoteFile)
                        }
                    },
                    onError: (error) => {
                        setIsDeletingProcess(false)
                    },
                }
            );
        }
    }

    const handleUserFileDelete = (file: MediaResponse) => {
        const id = file.id
        if (id) {
            setIsDeletingProcess(true)
            deleteAppealFileMutation.mutate(
                { appealId: appeal.id, data: { ids: [id] } },
                {
                    onSuccess: (data) => {
                        handleUserFileDeleted(file)
                        setIsDeletingProcess(false)
                        onDidUserFileDeleted()
                    },
                    onError: (error) => {
                        setIsDeletingProcess(false)
                    },
                }
            );
        }
    }

    useEffect(() => {
        setUserFiles(appeal.media)
        setAppealStatus(appeal.status)
        refetchReplies()
    }, [appeal]);

    const updateStatus = async (newStatus: FeedbackStatus) => {
        setLoadingStatus(true);

        setAppealStatus(newStatus)
        const data: AppealStatusUpdateRequest = {
            ids: [appeal.id],
            status: newStatus
        }

        try {
            await updateAppealStatus(data);
            onDidAnswerStatusChanged();
        } catch (error) {
        } finally {
            setLoadingStatus(false);
        }
    };

    const isAllowReply = isAllowAction(profile, 'REPLY_APPEALS')

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title="Ответить на обращение"
            size="middle"
            headerSize="regular"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <Stack direction="horizontal" align="center" justify="space-between" gap="condensed">
                        <FeedbackStatusButton
                            isDisabled={isAllowReply ? loadingStatus : true}
                            currentStatus={appealStatus}
                            onStatusChange={updateStatus}
                        />
                        <Text className='text-[14px] text-aloix-grey break-words'>
                            {formatDateTime(appeal.createdAt, ',')}
                        </Text>
                    </Stack>

                    <AppealCard
                        key={`user-appeal-${appeal.id}`}
                        data={appeal}
                    />

                    {/* Отображаем файлы из обращения */}
                    {userFiles.map((file, index) => (
                        <FileListComponent
                            key={`user-media-${index}`}
                            media={file}
                            isAllowDelete={true}
                            isDeletingProcess={isDeletingProcess}
                            onFileDidDeleted={() => handleUserFileDelete(file)}
                        />
                    ))}

                    { }
                    {dataReplies?.list.map((reply) => (
                        <Stack>
                            <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                                Ответ
                            </Text>
                            <AppealCard
                                key={`reply-appeal-${reply.id}`}
                                data={reply}
                            />
                            {
                                reply.media.map((file, index) => (
                                    <FileListComponent
                                        key={`reply-media--${index}`}
                                        media={file}
                                        isAllowDelete={false}
                                        isDeletingProcess={isDeletingProcess}
                                        onFileDidDeleted={() => handleUserFileDelete(file)}
                                    />
                                ))
                            }
                        </Stack>
                    ))}

                    {isAllowReply && (
                        <>
                            <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                                Ответ
                            </Text>
                            <FormControl>
                                <FormControl.Label className='invisible'></FormControl.Label>
                                <Textarea
                                    value={commentText}
                                    resize="none"
                                    placeholder='Напишите ответ на обращение'
                                    cols={60}
                                    rows={10}
                                    maxLength={1000}
                                    contrast={true}
                                    onChange={handleCommentChange}
                                />
                            </FormControl>

                            {/* Отображаем загруженные файлы */}
                            {files.map((file, index) => (
                                <FileListComponent
                                    key={`uploaded-${index}`}
                                    media={file}
                                    isAllowDelete={true}
                                    isDeletingProcess={isDeletingProcess}
                                    onFileDidDeleted={() => handleFileDeleted(file)}
                                />
                            ))}

                            {/* Отображаем загружающиеся файлы */}
                            {uploadingFiles.map((uploadingFile, index) => (
                                <FileListUploadedComponent
                                    key={`uploading-${index}`}
                                    file={uploadingFile.file}
                                    isUploading={true}
                                    progress={uploadingFile.progress}
                                    onCancel={() => handleUploadCancel(uploadingFile.file.name)}
                                    onFileDidDeleted={() => handleUploadDelete(uploadingFile)}
                                />
                            ))}

                            <FileUploadComponent
                                isDisabledUploadingButton={files.length + uploadingFiles.length >= 5}
                                onFileUploaded={fileDidUpload}
                                onUploadStart={handleUploadStart}
                                onUploadProgress={handleUploadProgress}
                                onUploadCancel={handleUploadCancel}
                            />
                        </>
                    )}

                    <Stack direction="horizontal" gap='normal' justify='end'>
                        <div></div>
                        <Button
                            onClick={onClose}
                            size='small'
                            variant="default">
                            Отмена
                        </Button>
                        {
                            isAllowReply && (
                                <Button
                                    onClick={sendAnswer}
                                    size='small'
                                    variant="primary"
                                    loading={isPending}
                                    disabled={isPending ||
                                        !commentText.trim() ||
                                        commentText.trim().length > 1000 ||
                                        !isAllowReply
                                    }>
                                    Отправить
                                </Button>
                            )
                        }
                    </Stack>
                </Stack>
            )}
        </ModalSheet>
    );
};